import { useState } from 'react';
import { TFunction } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import DetailsSection from '../../../componentsv2/DetailsSection';
import CopyToClipboardButton from '../../../componentsv2/CopyToClipboardButton';
import InfoDrawer from '../../../componentsv2/InfoDrawer';
import { SelectField } from '../../../componentsv2/Field';
import CurrencyAdornment from '../../../componentsv2/Field/CurrencyAdornment';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import Button from '../../../componentsv2/Button';
import Chip from '../../../componentsv2/Chip';
import { GlobalAccountDetails } from '../ConnectedAccountDetail/GlobalAccountsTable';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { formatNumericValue } from '../../../utils/common';
import { bankingEvents } from '../../../constants/events';
import { BankingCurrency, BankingDetails, GlobalAccount, GlobalAccountStatus } from '../../../types/banking';
import { COLORS } from '../../../themev2';
import { getCurrenciesFromGlobalAccount, getRoutingCodesFromGlobalAccount } from '../../../utils/banking';

function getAccountMarcoPaySummaryDetails(globalAccount: any, t: TFunction) {
  return [
    {
      label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.marcoPayNumber'),
      value: globalAccount.marcoPayNumber,
      formatValue: (value: string) => `***${value.slice(-4)}`,
      copyable: true,
    },
    {
      label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.marcoAccountName'),
      value: globalAccount.businessName,
      copyable: true,
    },
  ];
}

function getAccountSummaryDetails(globalAccount: GlobalAccount, t: TFunction) {
  const [currency, _] = getCurrenciesFromGlobalAccount(globalAccount);
  const routingCodes = getRoutingCodesFromGlobalAccount(globalAccount, currency!);
  if (currency === 'USD' && routingCodes) {
    return [
      ...routingCodes.map(({ type, value }) => ({
        label: t(`banking:connectedAccountDetails.detailsDrawer.accountDetails.${type}`),
        value,
        copyable: true,
      })),
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankAccountNumber'),
        value: globalAccount.accountNumber,
        formatValue: (value: string) => `***${value.slice(-4)}`,
        copyable: true,
      },
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankName'),
        value: globalAccount.institution?.name ?? '',
        copyable: true,
      },
    ];
  } else if (currency === 'MXN') {
    return [
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankAccountNumber'),
        value: globalAccount.accountNumber,
        copyable: true,
      },
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankName'),
        value: globalAccount.institution?.name ?? '',
        copyable: true,
      },
    ];
  } else if (currency === 'EUR') {
    return [
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.iban'),
        value: globalAccount.iban,
        copyable: true,
      },
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankName'),
        value: globalAccount.institution?.name ?? '',
        copyable: true,
      },
    ];
  }

  return [
    {
      label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.iban'),
      value: globalAccount.iban,
      copyable: true,
    },
    {
      label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankName'),
      value: globalAccount.institution?.name ?? '',
      copyable: true,
    },
  ];
}

function AlternativeReceiveAccounts({
  t,
  currency,
  globalAccounts,
}: {
  t: TFunction;
  currency: BankingCurrency;
  globalAccounts: GlobalAccount[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Box>
      <Button
        type="link"
        color="secondary"
        endIcon={isOpen ? 'arrowUp' : 'arrowDown'}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {t('banking-page:addFundsDrawer.account.alsoReceiveVia', {
          count: globalAccounts.length,
          currency,
        })}
      </Button>
      {isOpen && (
        <Box sx={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {globalAccounts.map((globalAccount) => {
            const routingCodes = getRoutingCodesFromGlobalAccount(globalAccount, currency);
            return (
              <Section
                key={globalAccount.id}
                sx={{ boxShadow: undefined, background: COLORS.gray[200], padding: '12px' }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  {t(`common:countries.${globalAccount.countryCode.toLowerCase()}`)} —{' '}
                  {globalAccount.accountNumber || globalAccount.iban || '-'}
                </Typography>
                {routingCodes.map(({ type, value }) => (
                  <Typography key={type} sx={{ fontSize: '13px' }}>
                    <strong>
                      {t(`banking:connectedAccountDetails.detailsDrawer.accountDetails.${type}`)}:
                    </strong>{' '}
                    {value}
                  </Typography>
                ))}
              </Section>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function getClipboardText(globalAccount: GlobalAccount, t: TFunction) {
  const details = getAccountSummaryDetails(globalAccount, t);
  return details.map(({ label, value }) => `${label}: ${value}`).join('\n');
}

export default function AddFundsDrawer({
  show,
  handleClose,
  t,
  connectedAccount,
}: {
  show: boolean;
  handleClose: () => void;
  t: TFunction;
  connectedAccount: BankingDetails;
}) {
  const { language } = useUserLocation();
  const accounts = connectedAccount.accountBalances;
  const initialSelectedAccount =
    accounts.find(
      (a) =>
        a.globalAccounts?.[0] &&
        a.globalAccounts?.[0].status === GlobalAccountStatus.ACTIVE &&
        a.currency === 'USD'
    ) ||
    accounts.find(
      (a) => a.globalAccounts?.[0] && a.globalAccounts?.[0].status === GlobalAccountStatus.ACTIVE
    ) ||
    null;
  const [selectedAccount, setSelectedAccount] = useState(initialSelectedAccount);
  const [showAllDetails, setShowAllDetails] = useState(false);

  return (
    <InfoDrawer showDrawer={show} handleClose={handleClose}>
      <Grid container sx={{ color: COLORS.gray[1200] }}>
        <Grid item xs={12}>
          <Typography variant="h5">{t('banking-page:addFundsDrawer.title')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography>{t('banking-page:addFundsDrawer.description')}</Typography>
        </Grid>
        {selectedAccount?.globalAccounts?.[0]?.status === GlobalAccountStatus.ACTIVE ? (
          <>
            <Grid item xs={12}>
              <Section
                sx={{
                  boxShadow: undefined,
                  background: COLORS.gray[300],
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <SelectField
                  name="account"
                  label={t('banking-page:addFundsDrawer.account.label')}
                  helperText={
                    <Typography>
                      {t('banking-page:addFundsDrawer.account.helperText')}{' '}
                      {formatNumericValue(selectedAccount.availableAmount, 2, language)}{' '}
                      {selectedAccount.currency}
                    </Typography>
                  }
                  options={accounts
                    .filter(
                      ({ globalAccounts }) => globalAccounts?.[0]?.status === GlobalAccountStatus.ACTIVE
                    )
                    .map(({ globalAccounts, currency }) => ({
                      label: `***${globalAccounts?.[0]?.accountNumber.slice(-4)} - ${currency}`,
                      value: currency,
                    }))}
                  renderValue={(value) => {
                    const globalAccount = accounts.find((a) => a.currency === value)?.globalAccounts?.[0];
                    return `***${globalAccount?.accountNumber.slice(-4)}`;
                  }}
                  value={selectedAccount.currency}
                  onChange={(value) => {
                    logEvent(bankingEvents.ADD_FUNDS_CHANGE_ACCOUNT, { account: value });
                    setSelectedAccount(accounts.find((a) => a.currency === value)!);
                  }}
                  startAdornment={<CurrencyAdornment currency={selectedAccount.currency} />}
                />
                {selectedAccount?.globalAccounts?.[0] && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '13px', color: COLORS.gray[1100] }}>
                      {t('banking-page:addFundsDrawer.account.alsoSupports')}
                    </Typography>
                    {selectedAccount.globalAccounts[0].currencies
                      .filter((currency) => currency !== selectedAccount.currency)
                      .map((currency) => (
                        <Chip key={currency} size="small" label={currency} />
                      ))}
                  </Box>
                )}
                <DetailsSection
                  key={`account-details-${selectedAccount?.globalAccounts?.[0]?.accountNumber}`}
                  details={getAccountSummaryDetails(selectedAccount?.globalAccounts?.[0], t)}
                  sx={{
                    boxShadow: undefined,
                    border: `1px solid ${COLORS.gray[900]}`,
                    background: '#FFFFFF',
                  }}
                />
                {selectedAccount?.globalAccounts && selectedAccount.globalAccounts.length > 1 && (
                  <AlternativeReceiveAccounts
                    t={t}
                    currency={selectedAccount.currency}
                    globalAccounts={selectedAccount.globalAccounts.slice(1)}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    type="link"
                    color="secondary"
                    endIcon={showAllDetails ? 'arrowUp' : 'arrowDown'}
                    onClick={() => {
                      logEvent(bankingEvents.ADD_FUNDS_TOGGLE_MORE_DETAILS, { value: !showAllDetails });
                      setShowAllDetails((prev) => !prev);
                    }}
                  >
                    {t('banking-page:addFundsDrawer.account.viewMoreDetails')}
                  </Button>
                  <Box sx={{ marginLeft: 'auto', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    {t('banking-page:addFundsDrawer.account.copyAllDetails')}
                    <CopyToClipboardButton
                      textToCopy={getClipboardText(selectedAccount?.globalAccounts?.[0], t)}
                      onClick={() => {
                        logEvent(bankingEvents.ADD_FUNDS_CLICK_COPY_ALL);
                      }}
                    />
                  </Box>
                </Box>
              </Section>
            </Grid>
            <Grid item xs={12}>
              <Section
                sx={{
                  boxShadow: undefined,
                  background: COLORS.gray[300],
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {selectedAccount?.globalAccounts?.[0] && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ fontSize: '18px', color: COLORS.gray[1100] }}>
                        {t('banking-page:addFundsDrawer.account.marcoPay.title')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ fontSize: '13px', color: COLORS.gray[1100] }}>
                        {t('banking-page:addFundsDrawer.account.marcoPay.description')}
                      </Typography>
                    </Box>
                    <DetailsSection
                      key={`account-details-${selectedAccount?.globalAccounts?.[0]?.accountNumber}`}
                      details={getAccountMarcoPaySummaryDetails(connectedAccount, t)}
                      sx={{
                        boxShadow: undefined,
                        border: `1px solid ${COLORS.gray[900]}`,
                        background: '#FFFFFF',
                      }}
                    />
                  </>
                )}
              </Section>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Section
              sx={{
                boxShadow: undefined,
                background: COLORS.gray[300],
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Typography>{t('banking-page:addFundsDrawer.account.noActiveAccounts')}</Typography>
            </Section>
          </Grid>
        )}
        {showAllDetails && selectedAccount?.globalAccounts?.[0]?.status === GlobalAccountStatus.ACTIVE && (
          <Grid item xs={12}>
            <GlobalAccountDetails
              globalAccount={selectedAccount.globalAccounts?.[0]}
              t={t}
              showTitle={false}
              trackingEvent={bankingEvents.ADD_FUNDS_CLICK_COPY}
            />
          </Grid>
        )}
      </Grid>
    </InfoDrawer>
  );
}
